import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './AuthPage.css';

const AuthPage = () => {
  const [mode, setMode] = useState('company-register'); // 'company-register', 'driver-login'
  const [formData, setFormData] = useState({
    companyName: '',
    adminName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Add loaded class for animations
    const timer = setTimeout(() => {
      document.querySelector('.auth-page')?.classList.add('loaded');
    }, 100);

    if (isAuthenticated && user?.profile) {
      // Redirect based on role
      if (user.profile.role === 'ADMIN') {
        navigate('/admin');
      } else if (user.profile.role === 'DRIVER') {
        navigate('/driver');
      }
    }

    return () => clearTimeout(timer);
  }, [isAuthenticated, user, navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleCompanyRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.companyName || !formData.adminName || !formData.email || !formData.password) {
      setError('Všetky polia sú povinné');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Heslá sa nezhodujú');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Heslo musí mať aspoň 6 znakov');
      setLoading(false);
      return;
    }

    const result = await register(
      formData.companyName,
      formData.adminName,
      formData.email,
      formData.password
    );

    if (result.success) {
      setSuccess(result.message);
      setFormData({
        companyName: '',
        adminName: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      // Auto switch to login mode after successful registration
      setTimeout(() => {
        setMode('driver-login');
        setSuccess('');
      }, 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleDriverLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.email || !formData.password) {
      setError('Email a heslo sú povinné');
      setLoading(false);
      return;
    }

    const result = await login(formData.email, formData.password);

    if (result.success) {
      // Navigation is handled by useEffect
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      adminName: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  return (
    <div className="auth-page">
      <div className="page-background"></div>
      
      <a href="/" className="back-button fade-in-left">
        <span className="back-arrow">←</span>
        <span className="back-text">Späť na hlavnú stránku</span>
      </a>

      <div className="auth-container">
        <div className="auth-brand fade-in-up">
          <h1 className="brand-logo">Dispetly</h1>
          <p className="brand-tagline">AI Dispečer pre Taxiky</p>
        </div>

        <div className="auth-card fade-in-up-delay">
          <div className="auth-header">
            <div className="auth-icon">
              <div className="security-key-icon">🔐</div>
            </div>
            <h2 className="auth-title">
              {mode === 'company-register' ? 'Registrácia Firmy' : 'Prihlásenie'}
            </h2>
            <p className="auth-subtitle">
              {mode === 'company-register' 
                ? 'Zaregistrujte svoju taxi firmu a začnite používať AI dispečing'
                : 'Prihláste sa do svojho účtu'
              }
            </p>
          </div>

          <div className="auth-mode-selector">
            <button 
              className={mode === 'company-register' ? 'active' : ''}
              onClick={() => switchMode('company-register')}
            >
              🏢 Registrácia Taxi Firmy
            </button>
            <button 
              className={mode === 'driver-login' ? 'active' : ''}
              onClick={() => switchMode('driver-login')}
            >
              🚕 Prihlásenie Taxikára
            </button>
          </div>

          {mode === 'company-register' ? (
            <form onSubmit={handleCompanyRegister} className="auth-form">
              <div className="form-group">
                <label>Názov taxi firmy</label>
                <input
                  type="text"
                  name="companyName"
                  placeholder="napr. Taxi Levice s.r.o."
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Meno administrátora</label>
                <input
                  type="text"
                  name="adminName"
                  placeholder="Vaše meno a priezvisko"
                  value={formData.adminName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="vas@email.sk"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Heslo</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Minimálne 6 znakov"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Potvrďte heslo</label>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Zopakujte heslo"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <button 
                type="submit" 
                className="auth-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner">⟳</span>
                    <span className="button-text">Registrujeme firmu...</span>
                  </>
                ) : (
                  <span className="button-text">Registrovať firmu</span>
                )}
              </button>

              <div className="registration-info">
                <h3>Čo získate registráciou:</h3>
                <ul>
                  <li>AI dispečing pre vašu taxi firmu</li>
                  <li>Automatické spracovanie hovorov zákazníkov</li>
                  <li>Efektívne rozdeľovanie objednávok vodičom</li>
                  <li>Zníženie prevádzkových nákladov</li>
                  <li>24/7 dostupnosť pre zákazníkov</li>
                </ul>
              </div>
            </form>
          ) : (
            <form onSubmit={handleDriverLogin} className="auth-form">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="vas@email.sk"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Heslo</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Vaše heslo"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button 
                type="submit" 
                className="auth-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner">⟳</span>
                    <span className="button-text">Prihlasujeme...</span>
                  </>
                ) : (
                  <span className="button-text">Prihlásiť sa</span>
                )}
              </button>

              <div className="auth-links">
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  setError('Funkcia bude dostupná neskôr');
                }}>
                  Zabudli ste heslo?
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage; 